'use client'

import Section from 'components/Section'
import Ok from 'components/Svgs/Ok'
import { MediaImageStandard, Typography } from 'components/Contentful'
import { AchieveLink } from 'components/AchieveLink/AchieveLink'
import { Button } from '@achieve/ascend'
import { useState } from 'react'
import classNames from 'classnames'
import { handleTrackAndReactEvent } from 'utils/analytics'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import BottomDisplay from './BottomDisplay'
import BorrowBox from './BorrowBox'
import { CheckCircle } from 'react-feather'
import { SliderWithCta } from '../HeroSliderAPL/SliderWithCta'

const VARIATION_SLIDER = 'slider'

const createImageContent = (src) => ({
  fields: {
    file: {
      url: src,
      fileName: 'hero-json-image',
    },
  },
})

function HeroJson(content) {
  const sectionVariationObject = {
    ...content?.content?.fields?.experiment,
    variant: content?.content?.fields?.experimentVariation,
  }
  const jsonContent =
    content?.content?.fields?.sectionContents?.find(
      (section) => section?.fields?.uiComponent === 'JsonContent'
    )?.fields?.jsonContent ||
    content?.content?.jsonContent ||
    content?.jsonContent

  const {
    eyebrow,
    title,
    firstCheck,
    secondCheck,
    thirdCheck,
    firstCheck2,
    secondCheck2,
    thirdCheck2,
    ctaText,
    ctaLink,
    secondaryCtaLink,
    disclaimerText,
    disclaimerTextClassName,
    disclaimerLinkText,
    disclaimerLinkTextClassName,
    disclaimerLinkHref,
    disclaimerSpacing,
    firstTab,
    secondTab,
    image,
    imageMobile,
    bottomDisplayItems,
    spin,
    variation = 'default',
    microCopy,
    sliderConfig,
    mobileSliderConfig,
    variant,
    personImage,
    chartImage,
    boxBackgroundImage,
    borrowBox,
  } = jsonContent

  const isBorrowBox = variant === 'borrowBox'
  const isSlider = !isBorrowBox && variation === VARIATION_SLIDER
  // Slider card shown only below breakpointLg; desktop layout stays the default one.
  const hasMobileSlider = !isBorrowBox && !isSlider && Boolean(mobileSliderConfig)
  const isCenteredOnTablet = isBorrowBox || isSlider || hasMobileSlider

  const checks = [firstCheck, secondCheck, thirdCheck].filter(Boolean)
  const checks2 = [firstCheck2, secondCheck2, thirdCheck2].filter(Boolean)
  const tabs = [firstTab, secondTab].filter(Boolean)
  const [activeTab, setActiveTab] = useState(0)
  const { fireAndForget } = useTriggerEvents()
  const isSecondTabActive = Boolean(secondTab) && activeTab === 1
  const activeChecks = isSecondTabActive && checks2.length > 0 ? checks2 : checks
  const resolvedCtaLink = isSecondTabActive && secondaryCtaLink ? secondaryCtaLink : ctaLink

  const handleTabClick = (event, index, tab) => {
    setActiveTab(index)

    fireAndForget({
      event_type: 'button_click',
      ...handleTrackAndReactEvent(event, {
        list_name: 'Hero Tabs',
        click_id: tab,
        click_text: tab,
        nav_link_section: 'Hero Tabs',
        track_event: 'button_click',
        event_action: 'button_click',
        event_type: 'button_click',
        sectionVariationObject,
      }),
    })
  }

  // CMS-driven spacing (px) above the disclaimer. Applied inline because Tailwind v4 only
  // generates classes found in source, so arbitrary classes from Contentful would not exist.
  // With the mobile slider card, the spacing only applies from breakpointLg up (the card sits
  // between the checks and the disclaimer on smaller screens), so it goes through a CSS variable.
  const hasDisclaimerSpacing = disclaimerSpacing != null
  const disclaimerStyle = hasDisclaimerSpacing
    ? hasMobileSlider
      ? { '--hero-disclaimer-spacing': `${Number(disclaimerSpacing)}px` }
      : { marginTop: Number(disclaimerSpacing) }
    : undefined

  const disclaimer = disclaimerText && (
    <div
      className={classNames(
        'inline-flex items-baseline gap-1 whitespace-nowrap',
        isSlider && 'pt-4',
        hasMobileSlider &&
          hasDisclaimerSpacing &&
          'breakpointLg:mt-[var(--hero-disclaimer-spacing)]'
      )}
      style={disclaimerStyle}
    >
      <Typography
        component="span"
        responsive={true}
        className={
          disclaimerTextClassName ||
          'font-ultramarine text-[16px] font-normal leading-[140%] text-left text-[#1D252F]'
        }
        content={disclaimerText}
      />
      {disclaimerLinkText && disclaimerLinkHref && (
        <AchieveLink
          href={disclaimerLinkHref}
          track={{
            list_name: 'Home Hero Direct Mailer Click',
            click_id: disclaimerLinkText,
            click_text: disclaimerLinkText,
            click_url: disclaimerLinkHref,
          }}
        >
          <Typography
            component="span"
            responsive={true}
            className={
              disclaimerLinkTextClassName ||
              'font-ultramarine text-[16px] font-normal leading-[140%] text-left text-[#3300FF] underline'
            }
            content={disclaimerLinkText}
          />
        </AchieveLink>
      )}
    </div>
  )

  return (
    <Section
      disableGutters
      className="py-[48px] px-[20px] breakpointMd:py-[64px] breakpointMd:px-[32px] breakpointLg:py-[80px] breakpointLg:px-[64px]"
    >
      <div
        className={classNames(
          'grid grid-cols-4 breakpointMd:grid-cols-6 breakpointLg:grid-cols-8 breakpointXl:grid-cols-12',
          isSlider
            ? 'breakpointLg:gap-[2rem] breakpointXl:gap-[2rem]'
            : 'breakpointLg:gap-8 breakpointXl:gap-8'
        )}
      >
        <div
          className={classNames(
            isSlider
              ? 'col-span-12 breakpointLg:col-span-4 breakpointXl:col-span-5 breakpointMd:justify-center breakpointMd:items-center breakpointLg:justify-start breakpointLg:items-start'
              : classNames(
                  'col-span-4 breakpointMd:col-span-6 breakpointLg:col-span-4',
                  isBorrowBox
                    ? 'breakpointXl:col-span-5 breakpointMd:items-center breakpointLg:items-start'
                    : 'breakpointXl:col-span-6',
                  hasMobileSlider && 'breakpointMd:items-center breakpointLg:items-start'
                ),
            'flex flex-col justify-start items-start gap-6'
          )}
        >
          {eyebrow && (
            <Typography
              component="h1"
              responsive={true}
              variant="labelSm"
              mobileVariant="labelSm"
              className={classNames(
                'font-ultramarine text-[14px] font-bold leading-[120%] tracking-[3.5px] uppercase text-[#1D252F] m-0',
                isCenteredOnTablet && 'breakpointMd:text-center breakpointLg:text-left'
              )}
              content={eyebrow}
            />
          )}
          {title && (
            <Typography
              component={eyebrow ? 'h2' : 'h1'}
              responsive={true}
              variant="headingXl"
              mobileVariant="headingXl"
              className={classNames(
                'font-ultramarine font-bold text-[#1D252F] text-[40px] leading-[130%] breakpointMd:text-[52px] breakpointMd:leading-[107%] m-0',
                isCenteredOnTablet && 'breakpointMd:text-center breakpointLg:text-left'
              )}
              content={title}
            />
          )}
          {tabs.length > 0 && (
            <div
              className={classNames(
                'inline-flex items-center gap-1 rounded-[10px] bg-[#EFF2F8] py-1',
                activeTab === 0 ? 'pl-[5px] pr-1' : 'pl-1 pr-[5px]'
              )}
            >
              {tabs.map((tab, index) => {
                const isActive = activeTab === index
                return (
                  <button
                    key={`hero-json-tab-${index}`}
                    type="button"
                    onClick={(event) => handleTabClick(event, index, tab)}
                    className={classNames(
                      'rounded-[8px] px-4 py-2 font-ultramarine text-[14px] text-center text-[#42546B] cursor-pointer border-0 transition-colors',
                      isActive
                        ? 'bg-[#FFFFFF] font-bold leading-[120%] tracking-[0.28px]'
                        : 'bg-transparent font-normal leading-[140%]'
                    )}
                  >
                    {tab}
                  </button>
                )
              })}
            </div>
          )}
          {activeChecks.length > 0 && (
            <div className="flex items-center justify-start">
              <ul className="m-0 p-0 transition-all duration-100 transition-discrete ease-in-out">
                {activeChecks.map((check, index) => (
                  <li
                    key={`hero-json-check-${index}`}
                    className={classNames(
                      'flex flex-row flex-nowrap text-left mb-4 last:mb-0 transition-all duration-100 transition-discrete ease-in-out',
                      isCenteredOnTablet &&
                        'justify-start breakpointMd:justify-center breakpointLg:justify-start'
                    )}
                  >
                    <Ok className="h-[25px] w-[25px] text-content-primary" />
                    <Typography
                      responsive={true}
                      variant="bodyMd"
                      mobileVariant="bodySm"
                      className="pl-2 font-ultramarine font-normal leading-[140%] text-[16px] breakpointMd:text-[18px] text-[#1D252F]"
                      content={check}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
          {isSlider && sliderConfig && (
            <div className="w-full md:ml-8 md:w-[40%] flex-col items-center flex breakpointLg:hidden">
              <div className="pl-0 w-full flex flex-col items-center">
                <div
                  className="shadow bg-white-100 pt-[24px] px-8 sm:py-8 border border-solid border-blue-100 rounded-2xl w-full sm:max-w-[417px] sm:max-h-[307px] min-w-[335px] min-h-[230px] max-w-[335px] max-h-[264px] bg-white [&_.MuiSlider-track]:bg-[#3B6AF7] [&_.MuiSlider-track]:border-[#3B6AF7] [&_.MuiSlider-thumb]:bg-[#fff] [&_.MuiSlider-thumb]:border-[#C0CBD8] [&_.MuiSlider-thumb]:border [&_.MuiSlider-thumb]:border-solid"
                  data-testid="hero-slider-apl-form-container-mobile"
                >
                  <Typography
                    content={sliderConfig.title}
                    variant="bodyMd"
                    mobileVariant="bodySm"
                    responsive={true}
                    className="text-center mb-[12px] md:mb-4 text-nowrap text-[#42546B]"
                  />
                  <SliderWithCta
                    {...{
                      ...sliderConfig,
                      sectionVariationObject,
                      stylesClassOverride: {
                        title: 'text-[#1D252F] text-center md:mb-2 text-[24px] lg:text-[32px]',
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          )}
          {hasMobileSlider && (
            <div className="w-full flex justify-center breakpointLg:hidden">
              <div
                className="shadow bg-white border border-solid border-blue-100 rounded-2xl w-full max-w-[335px] breakpointMd:max-w-[417px] py-6 px-[26px] breakpointMd:py-8 box-border [&_.MuiSlider-track]:bg-[#3B6AF7] [&_.MuiSlider-track]:border-[#3B6AF7] [&_.MuiSlider-thumb]:bg-[#fff] [&_.MuiSlider-thumb]:border-[#C0CBD8] [&_.MuiSlider-thumb]:border [&_.MuiSlider-thumb]:border-solid"
                data-testid="hero-json-mobile-slider-card"
              >
                <Typography
                  content={mobileSliderConfig.title}
                  variant="bodyMd"
                  mobileVariant="bodySm"
                  responsive={true}
                  className="text-center mb-4 text-[#42546B]"
                />
                <SliderWithCta
                  {...{
                    ...mobileSliderConfig,
                    sectionVariationObject,
                    stylesClassOverride: {
                      title: 'text-[#1D252F] text-center text-[24px] breakpointMd:text-[32px]',
                    },
                  }}
                />
              </div>
            </div>
          )}
          {!isBorrowBox && ctaText && resolvedCtaLink && (
            <AchieveLink
              href={resolvedCtaLink}
              track={{
                list_name: 'Hero',
                click_id: ctaText,
                click_text: ctaText,
                click_url: resolvedCtaLink,
                nav_link_section: 'Hero',
                track_event: 'button_click',
                event_action: 'button_click',
                event_type: 'button_click',
                sectionVariationObject,
              }}
            >
              <Button color="primary" variant="contained">
                {ctaText}
              </Button>
            </AchieveLink>
          )}
          {isBorrowBox ? <div className="hidden breakpointLg:block">{disclaimer}</div> : disclaimer}
        </div>
        {isSlider && sliderConfig && (
          <div className="w-full md:ml-8 md:w-[417px] pt-8 md:pt-0 relative z-[1] top-[15%] breakpointXl:top-[34%] right-[70%] hidden breakpointLg:block">
            <div className="pl-0 md:ml-[80px] w-full flex flex-col items-center">
              <div
                className="shadow bg-white-100 pt-[24px] px-8 sm:py-8 border border-solid border-blue-100 rounded-2xl w-full sm:max-w-[417px] sm:max-h-[307px] min-w-[335px] min-h-[264px] max-w-[335px] max-h-[264px] bg-white [&_.MuiSlider-track]:bg-[#3B6AF7] [&_.MuiSlider-track]:border-[#3B6AF7] [&_.MuiSlider-thumb]:bg-[#fff] [&_.MuiSlider-thumb]:border-[#C0CBD8] [&_.MuiSlider-thumb]:border [&_.MuiSlider-thumb]:border-solid"
                data-testid="hero-slider-apl-form-container"
              >
                <Typography
                  content={sliderConfig.title}
                  variant="bodyMd"
                  mobileVariant="bodySm"
                  responsive={true}
                  className="text-center mb-[12px] md:mb-4 text-nowrap text-[#42546B]"
                />
                <SliderWithCta
                  {...{
                    ...sliderConfig,
                    sectionVariationObject,
                    stylesClassOverride: {
                      title: 'text-[#1D252F] text-center md:mb-2 text-[24px] lg:text-[32px]',
                    },
                  }}
                />
              </div>
            </div>
          </div>
        )}
        <div
          className={classNames(
            'justify-center items-start',
            isBorrowBox
              ? 'col-span-4 breakpointMd:col-span-6 breakpointLg:col-span-4 breakpointXl:col-span-7 min-w-0 flex mt-8 breakpointLg:mt-0'
              : 'hidden breakpointLg:flex breakpointLg:col-span-4 breakpointXl:col-span-6'
          )}
        >
          {isBorrowBox ? (
            <BorrowBox
              personImage={personImage}
              chartImage={chartImage}
              boxBackgroundImage={boxBackgroundImage}
              borrowBox={borrowBox}
              sectionVariationObject={sectionVariationObject}
            />
          ) : (
            <>
              {imageMobile && (
                <div className="hidden breakpointLg:block breakpointXl:hidden w-full max-w-[480px]">
                  <MediaImageStandard
                    content={createImageContent(imageMobile)}
                    alt="Hero image mobile"
                    width={480}
                    height={420}
                    className="w-full h-auto"
                    priority
                  />
                </div>
              )}
              {image && (
                <div className="hidden breakpointXl:block w-full max-w-[640px]">
                  <MediaImageStandard
                    content={createImageContent(image)}
                    alt="Hero image"
                    width={640}
                    height={520}
                    className="w-full h-auto"
                    priority
                  />
                </div>
              )}
            </>
          )}
        </div>
        {isBorrowBox && disclaimer && (
          <div className="col-span-4 breakpointMd:col-span-6 breakpointLg:hidden mt-[24px] breakpointMd:flex breakpointMd:justify-center">
            {disclaimer}
          </div>
        )}
      </div>
      {microCopy && microCopy.length > 0 && (
        <div
          className={classNames(
            isSlider
              ? 'flex flex-col breakpointMd:flex-row flex-wrap items-start breakpointMd:items-center justify-start breakpointXl:justify-center gap-x-4 gap-y-4 md:gap-y-2 mt-[24px] breakpointLg:mt-[48px] mb-[24px] md:mb-0'
              : 'flex flex-wrap items-center justify-start breakpointXl:justify-center gap-x-4 gap-y-2 mt-[24px] breakpointLg:mt-[48px]',
            isCenteredOnTablet &&
              !isSlider &&
              'breakpointMd:justify-center breakpointLg:justify-start'
          )}
        >
          {microCopy.map((item, index) => (
            <div key={`trust-item-${index}`} className="flex items-center" style={{ gap: '4px' }}>
              <CheckCircle
                size={14}
                className="shrink-0 text-[#42546B] breakpointMd:w-[14px] breakpointMd:h-[14px]"
              />
              <span
                className={classNames(
                  'font-ultramarine font-normal breakpointMd:font-medium leading-[120%] ',
                  isSlider
                    ? 'text-[14px] text-[#1D252F]'
                    : 'text-[12px] breakpointMd:text-[14px] text-[#42546B]'
                )}
              >
                {item}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-[24px] breakpointMd:mt-[48px] breakpointLg:mt-[48px]">
        <BottomDisplay items={bottomDisplayItems || []} spin={Boolean(spin)} />
      </div>
    </Section>
  )
}

export default HeroJson
